<script
  setup
  lang="ts"
  generic="
    T extends Record<string, string | number | undefined | null>,
    U extends keyof T
  "
>
import type { HTMLAttributes } from 'vue'
import type { titleClasses } from '.'
import { Motion } from 'motion-v'

const props = withDefaults(
  defineProps<{
    class?: HTMLAttributes['class']
    imageClass?: HTMLAttributes['class']
    items?: T[] | string[]
    itemTitleKey?: U
    itemSubtitleKey?: U
    itemIconKey?: U
    itemIcon?: string
    heroImage?: string
    buttonLabel?: string
    buttonLink?: string
    buttonClass?: HTMLAttributes['class']
    withGradient?: boolean
    withAction?: boolean
    title?: string
    subTitle?: string
    description?: string
    titleVariantSize?: keyof typeof titleClasses
    containerFullWidth?: boolean
  }>(),
  {
    class: undefined,
    imageClass: undefined,
    items: undefined,
    itemIconKey: () => 'icon' as U,
    itemTitleKey: () => 'title' as U,
    itemSubtitleKey: () => 'description' as U,
    itemIcon: 'material-symbols:verified',
    heroImage: '/assets/home-v4/hero.webp',
    buttonLabel: 'Daftar Gratis',
    buttonLink: 'https://app.kiriminaja.com/register',
    buttonClass: undefined,
    title: 'Hero Title',
    subTitle: undefined,
    description: undefined,
    withGradient: false,
    withAction: true,
    titleVariantSize: 'small'
  }
)
</script>

<template>
  <section
    :class="
      cn(
        'h-full py-6 lg:py-12 w-full bg-background dark:bg-surface-container',
        {
          'bg-[linear-gradient(to_right,var(--color-surface-container-low)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-surface-container-low)_1px,transparent_1px)] bg-[size:3.9rem_2.9rem] lg:bg-[size:5rem_4rem]':
            props.withGradient
        },
        props.class
      )
    "
  >
    <UiContainer
      class="grid lg:grid-cols-2 grid-cols-1 gap-6 lg:gap-10 items-center"
      :full-width="containerFullWidth"
    >
      <slot>
        <div class="flex flex-col gap-3">
          <Motion
            :initial="{ opacity: 0, translateY: 40 }"
            :while-in-view="{ opacity: 1, translateY: 0 }"
            :in-view-options="{ once: true }"
          >
            <UiSectionTitle
              :variant-size="titleVariantSize"
              :title="props.title"
              title-as="h2"
              :sub-title="props.subTitle"
              sub-title-as="p"
              :description="props.description"
            />
          </Motion>

          <slot name="list" :items="items">
            <div
              v-if="items"
              class="flex flex-col gap-4 lg:gap-5 my-3 text-lg leading-7"
            >
              <Motion
                v-for="(item, key) in items"
                :key="`r-${key}`"
                class="flex gap-2"
                :initial="{ opacity: 0, translateY: 30 }"
                :while-in-view="{ opacity: 1, translateY: 0 }"
                :in-view-options="{ once: true }"
                :class="
                  cn({
                    'items-start':
                      props.itemIconKey &&
                      typeof item !== 'string' &&
                      item[props.itemIconKey!]
                  })
                "
              >
                <span
                  class="shrink-0"
                  :class="{
                    'bg-secondary-container p-1.5 rounded-full':
                      props.itemIconKey &&
                      typeof item !== 'string' &&
                      item[props.itemIconKey!]
                  }"
                >
                  <Icon
                    :name="
                      (props.itemIconKey && typeof item !== 'string'
                        ? item[props.itemIconKey!]?.toString()
                        : props.itemIcon) || props.itemIcon
                    "
                    size="24px"
                    class="text-primary"
                  />
                </span>
                <div class="grow">
                  <div
                    class="text-sm lg:text-base flex-1"
                    :class="{
                      'font-semibold':
                        typeof item !== 'string' && item[props.itemSubtitleKey!]
                    }"
                  >
                    {{
                      typeof item === 'string'
                        ? item
                        : item[props.itemTitleKey!]
                    }}
                  </div>
                  <div
                    v-if="
                      typeof item !== 'string' && item[props.itemSubtitleKey!]
                    "
                    class="text-sm text-outline"
                  >
                    {{ item[props.itemSubtitleKey!] }}
                  </div>
                </div>
              </Motion>
            </div>
          </slot>
          <slot v-if="withAction" name="action">
            <Motion
              :initial="{ opacity: 0, translateY: 40 }"
              :while-in-view="{ opacity: 1, translateY: 0 }"
              :in-view-options="{ once: true }"
              class="flex flex-col sm:items-center sm:flex-row pb-2"
            >
              <UiButton
                :class="props.buttonClass"
                :to="$utm.utmUrl(props.buttonLink)"
                :label="props.buttonLabel"
              />
            </Motion>
          </slot>
        </div>
      </slot>
      <slot name="hero">
        <div
          :class="
            cn(
              'order-last w-full max-w-[70svw] lg:max-w-xl mx-auto lg:mr-0',
              props.imageClass
            )
          "
        >
          <NuxtImg
            :alt="props.title || 'hero-image'"
            width="576px"
            height="576px"
            loading="lazy"
            :src="props.heroImage"
          />
        </div>
      </slot>
    </UiContainer>
  </section>
</template>
